import React, { useState, useRef, useEffect } from 'react';

interface LoginProps {
  onLogin: (success: boolean) => void;
}

export function Login({ onLogin }: LoginProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Test if video file exists
    fetch('/assets/videos/login-background.mp4', { method: 'HEAD' })
      .then(response => {
        console.log('Video file check:', response.status === 200 ? 'EXISTS' : 'NOT FOUND');
        console.log('Response status:', response.status);
      })
      .catch(error => {
        console.error('Video file check failed:', error);
      });

    const video = videoRef.current;
    if (video) {
      const handleError = (e: Event) => {
        console.error('Background video failed to load:', e);
        console.error('Video error details:', video.error);
        if (video.error) {
          console.error('Error code:', video.error.code);
          console.error('Error message:', video.error.message);
        }
        setVideoError(true);
      };

      const handleLoadStart = () => {
        console.log('Video loading started...');
      };

      const handleCanPlay = () => {
        console.log('Video can play - loaded successfully');
      };

      const handleLoadedData = () => {
        console.log('Video data loaded');
      };

      video.addEventListener('error', handleError);
      video.addEventListener('loadstart', handleLoadStart);
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('loadeddata', handleLoadedData);
      
      return () => {
        video.removeEventListener('error', handleError);
        video.removeEventListener('loadstart', handleLoadStart);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('loadeddata', handleLoadedData);
      };
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simulate loading delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Check credentials (hardcoded for security)
    const validUsers = [
      { username: 'Remco', password: 'Zwaardvis-69' },
      { username: 'Raymond', password: 'Goudvis-77' },
      { username: 'Abe', password: 'Potvis-88' },
      { username: 'Joost', password: 'Haring-26' }
    ];

    const isValid = validUsers.some(
      user => user.username === username && user.password === password
    );

    if (isValid) {
      onLogin(true);
    } else {
      setError('Ongeldige gebruikersnaam of wachtwoord');
      onLogin(false);
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden">
      {/* Background video or fallback */}
      <div className="absolute inset-0 z-0">
        {!videoError ? (
          <>
            <video
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
              preload="metadata"
              onError={(e) => {
                console.error('Video onError triggered:', e);
                setVideoError(true);
              }}
            >
              <source 
                src="/assets/videos/login-background.mp4" 
                type="video/mp4"
                onError={(e) => console.error('MP4 source error:', e)}
              />
              <source 
                src="/assets/videos/login-background.webm" 
                type="video/webm"
                onError={(e) => console.error('WebM source error:', e)}
              />
            </video>
            {/* Dark overlay for better readability */}
            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          </>
        ) : (
          /* Fallback gradient background if video fails */
          <div className="w-full h-full bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900"></div>
        )}
        
      </div>
      {/* Login form with higher z-index */}
      <div className="relative z-10 max-w-md w-full bg-white bg-opacity-20 backdrop-blur-md rounded-lg shadow-xl p-6 mx-4 border border-white border-opacity-30">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white">FWa</h1>
          <h2 className="text-lg text-white text-opacity-90 mt-1">PDF Converter</h2>
          <p className="text-sm text-white text-opacity-80 mt-4">Inloggen om door te gaan</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-white text-opacity-90 mb-1">
              Gebruikersnaam
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Voer gebruikersnaam in"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-white text-opacity-90 mb-1">
              Wachtwoord
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Voer wachtwoord in"
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
              isLoading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Inloggen...
              </span>
            ) : (
              'Inloggen'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs text-white text-opacity-60">
            © 2024 FWa PDF Converter. Alle rechten voorbehouden.
          </p>
        </div>
      </div>
    </div>
  );
}